import { LeadNote } from '@spike/client-model';
import moment from 'moment-timezone';
import { FunctionComponent, useMemo } from 'react';
import LeadNoteCard from './LeadNoteCard';
import styles from './LeadNotesList.module.scss';

interface LeadNotesListProps {
    notes: Array<LeadNote>;
    timeZone: string;
    onEdit: (note: LeadNote) => void;
    onDelete: (note: LeadNote) => void;
}

interface NoteGroup {
    key: string;
    label: string;
    notes: Array<LeadNote>;
}

const groupByDay = (notes: Array<LeadNote>, timeZone: string): Array<NoteGroup> =>
    notes.reduce((groups: Array<NoteGroup>, note) => {
        const createdAt = moment(note.createdAt).tz(timeZone);
        const key = createdAt.format('DD-MM-YYYY');
        const existing = groups.find(group => group.key === key);

        if (existing) {
            existing.notes.push(note);

            return groups;
        }

        return [
            ...groups,
            {
                key,
                label: createdAt.isSame(moment().tz(timeZone), 'day')
                    ? 'Today'
                    : createdAt.format('dddd, D MMMM, YYYY'),
                notes: [note]
            }
        ];
    }, []);

const LeadNotesList: FunctionComponent<LeadNotesListProps> = ({ notes, timeZone, onEdit, onDelete }) => {
    const groups = useMemo(() => groupByDay(notes, timeZone), [notes, timeZone]);

    return (
        <div className={styles.root}>
            {groups.map(group => (
                <section
                    key={group.key}
                    className={styles.group}
                >
                    <p className={styles.groupLabel}>{group.label}</p>

                    {group.notes.map(note => (
                        <LeadNoteCard
                            key={note.id}
                            note={note}
                            timeZone={timeZone}
                            onEdit={onEdit}
                            onDelete={onDelete}
                        />
                    ))}
                </section>
            ))}
        </div>
    );
};

export default LeadNotesList;
